import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import Chart from "chart.js";
import api from "services/api.js";
import logger from "utils/logger";

export default function Dashboard() {
  const [dashboardStats, setDashboardStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const history = useHistory();

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  useEffect(() => {
    if (dashboardStats.length > 0) {
      renderCharts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardStats]);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/projects/dashboard/stats');
      if (response.data.success) {
        setDashboardStats(response.data.data);
      }
    } catch (err) {
      logger.error('Error fetching dashboard stats:', err);
      setError('Error al cargar las estadísticas del dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusClick = (projectCode, status) => {
    history.push(`/admin/tasks?project_code=${projectCode}&status=${status}`);
  };

  const handleProjectClick = (projectCode) => {
    history.push(`/admin/tasks?project_code=${projectCode}`);
  };

  const getStatusLabel = (status) => {
    const labels = {
      created: 'Creadas',
      pending: 'Pendientes',
      working: 'En curso',
      reviewing: 'En revisión',
      finished: 'Finalizadas',
      failed: 'Fallidas'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      created: 'bg-gray-500',
      pending: 'bg-yellow-500',
      working: 'bg-blue-500',
      reviewing: 'bg-purple-500',
      finished: 'bg-green-500',
      failed: 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      created: 'bg-gray-100 text-gray-800 hover:bg-gray-200',
      pending: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200',
      working: 'bg-blue-100 text-blue-800 hover:bg-blue-200',
      reviewing: 'bg-purple-100 text-purple-800 hover:bg-purple-200',
      finished: 'bg-green-100 text-green-800 hover:bg-green-200',
      failed: 'bg-red-100 text-red-800 hover:bg-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 hover:bg-gray-200';
  };

  const getStatusIconColor = (status) => {
    const colors = {
      created: 'text-gray-500',
      pending: 'text-yellow-500',
      working: 'text-blue-500',
      reviewing: 'text-purple-500',
      finished: 'text-green-500',
      failed: 'text-red-500'
    };
    return colors[status] || 'text-gray-500';
  };

  const calculateGlobalStats = () => {
    if (!dashboardStats.length) return null;

    const stats = {
      totalProjects: dashboardStats.length,
      totalTasks: 0,
      created: 0,
      pending: 0,
      working: 0,
      reviewing: 0,
      finished: 0,
      failed: 0
    };

    dashboardStats.forEach(project => {
      stats.totalTasks += project.total_tasks;
      Object.entries(project.tasks_by_status).forEach(([status, count]) => {
        if (stats[status] !== undefined) {
          stats[status] += count;
        }
      });
    });

    return stats;
  };

  const renderCharts = () => {
    const globalStats = calculateGlobalStats();
    if (!globalStats) return;

    // Destroy previous charts if they exist
    const chartIds = ['statusChart', 'projectsChart'];
    chartIds.forEach(id => {
      const canvas = document.getElementById(id);
      if (canvas && canvas.chart) {
        canvas.chart.destroy();
      }
    });

    // Status Distribution Chart (Doughnut)
    const statusCtx = document.getElementById('statusChart');
    if (statusCtx) {
      statusCtx.chart = new Chart(statusCtx, {
        type: 'doughnut',
        data: {
          labels: ['Creadas', 'Pendientes', 'En curso', 'En revisión', 'Finalizadas', 'Fallidas'],
          datasets: [{
            data: [
              globalStats.created,
              globalStats.pending,
              globalStats.working,
              globalStats.reviewing,
              globalStats.finished,
              globalStats.failed
            ],
            backgroundColor: [
              'rgba(107, 114, 128, 0.8)', // gray
              'rgba(234, 179, 8, 0.8)',   // yellow
              'rgba(59, 130, 246, 0.8)',  // blue
              'rgba(168, 85, 247, 0.8)',  // purple
              'rgba(34, 197, 94, 0.8)',   // green
              'rgba(239, 68, 68, 0.8)'    // red
            ],
            borderColor: [
              'rgb(107, 114, 128)',
              'rgb(234, 179, 8)',
              'rgb(59, 130, 246)',
              'rgb(168, 85, 247)',
              'rgb(34, 197, 94)',
              'rgb(239, 68, 68)'
            ],
            borderWidth: 2
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            position: 'bottom',
            labels: {
              padding: 15,
              fontSize: 12
            }
          },
          tooltips: {
            callbacks: {
              label: function(tooltipItem, data) {
                const dataset = data.datasets[tooltipItem.datasetIndex];
                const total = dataset.data.reduce((a, b) => a + b, 0);
                const currentValue = dataset.data[tooltipItem.index];
                const percentage = ((currentValue / total) * 100).toFixed(1);
                return data.labels[tooltipItem.index] + ': ' + currentValue + ' (' + percentage + '%)';
              }
            }
          }
        }
      });
    }

    // Projects Comparison Chart (Bar)
    const projectsCtx = document.getElementById('projectsChart');
    if (projectsCtx && dashboardStats.length > 0) {
      const projectNames = dashboardStats.map(p => p.project_code);
      const datasets = [
        {
          label: 'En curso',
          data: dashboardStats.map(p => p.tasks_by_status.working || 0),
          backgroundColor: 'rgba(59, 130, 246, 0.8)',
          borderColor: 'rgb(59, 130, 246)',
          borderWidth: 1
        },
        {
          label: 'Pendientes',
          data: dashboardStats.map(p => p.tasks_by_status.pending || 0),
          backgroundColor: 'rgba(234, 179, 8, 0.8)',
          borderColor: 'rgb(234, 179, 8)',
          borderWidth: 1
        },
        {
          label: 'Finalizadas',
          data: dashboardStats.map(p => p.tasks_by_status.finished || 0),
          backgroundColor: 'rgba(34, 197, 94, 0.8)',
          borderColor: 'rgb(34, 197, 94)',
          borderWidth: 1
        }
      ];

      projectsCtx.chart = new Chart(projectsCtx, {
        type: 'bar',
        data: {
          labels: projectNames,
          datasets: datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            xAxes: [{
              stacked: false,
              gridLines: {
                display: false
              }
            }],
            yAxes: [{
              stacked: false,
              ticks: {
                beginAtZero: true,
                stepSize: 1
              }
            }]
          },
          legend: {
            position: 'bottom',
            labels: {
              padding: 15,
              fontSize: 12
            }
          }
        }
      });
    }
  };

  if (loading) {
    return (
      <div className="flex flex-wrap">
        <div className="w-full px-4">
          <div className="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 shadow-lg rounded-lg p-8">
            <div className="text-center">
              <i className="fas fa-spinner fa-spin text-5xl text-lightBlue-500 mb-4"></i>
              <p className="text-lg text-blueGray-600">Cargando estadísticas del dashboard...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-wrap">
        <div className="w-full px-4">
          <div className="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 shadow-lg rounded-lg p-8">
            <div className="text-center">
              <i className="fas fa-exclamation-triangle text-5xl text-red-500 mb-4"></i>
              <p className="text-lg text-red-600">{error}</p>
              <button
                onClick={fetchDashboardStats}
                className="mt-4 bg-lightBlue-500 text-white active:bg-lightBlue-600 text-sm font-bold uppercase px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150"
              >
                <i className="fas fa-redo mr-2"></i>
                Reintentar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (dashboardStats.length === 0) {
    return (
      <div className="flex flex-wrap">
        <div className="w-full px-4">
          <div className="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 shadow-lg rounded-lg">
            <div className="rounded-t bg-white mb-0 px-6 py-6">
              <div className="text-center flex justify-between">
                <h6 className="text-blueGray-700 text-xl font-bold">Dashboard de Proyectos</h6>
              </div>
            </div>
            <div className="block w-full px-6 py-8">
              <div className="text-center py-12">
                <div className="max-w-md mx-auto">
                  <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-lightBlue-100 mb-4">
                    <i className="fas fa-project-diagram text-3xl text-lightBlue-600"></i>
                  </div>
                  <h3 className="mt-2 text-xl font-semibold text-blueGray-700">No hay proyectos activos</h3>
                  <p className="mt-2 text-base text-blueGray-500">
                    Comienza creando tu primer proyecto para ver las estadísticas aquí.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const globalStats = calculateGlobalStats();

  return (
    <>
      <div className="flex flex-wrap">
        <div className="w-full px-4 mb-6">
          <h1 className="text-3xl font-bold text-blueGray-700 mb-2">
            <i className="fas fa-chart-line mr-3 text-lightBlue-500"></i>
            Dashboard de Proyectos
          </h1>
          <p className="text-blueGray-500">Vista general de todos tus proyectos activos</p>
        </div>
      </div>

      {/* Global Stats Cards */}
      <div className="flex flex-wrap">
        <div className="w-full lg:w-6/12 xl:w-3/12 px-4 mb-6">
          <div className="relative flex flex-col min-w-0 break-words bg-white rounded-lg shadow-lg">
            <div className="flex-auto p-4">
              <div className="flex flex-wrap">
                <div className="relative w-full pr-4 max-w-full flex-grow flex-1">
                  <h5 className="text-blueGray-400 uppercase font-bold text-xs mb-1">
                    Total Proyectos
                  </h5>
                  <span className="font-bold text-3xl text-blueGray-700">
                    {globalStats.totalProjects}
                  </span>
                </div>
                <div className="relative w-auto pl-4 flex-initial">
                  <div className="text-white p-3 text-center inline-flex items-center justify-center w-12 h-12 shadow-lg rounded-full bg-lightBlue-500">
                    <i className="fas fa-folder-open"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full lg:w-6/12 xl:w-3/12 px-4 mb-6">
          <div className="relative flex flex-col min-w-0 break-words bg-white rounded-lg shadow-lg">
            <div className="flex-auto p-4">
              <div className="flex flex-wrap">
                <div className="relative w-full pr-4 max-w-full flex-grow flex-1">
                  <h5 className="text-blueGray-400 uppercase font-bold text-xs mb-1">
                    Total Tareas
                  </h5>
                  <span className="font-bold text-3xl text-blueGray-700">
                    {globalStats.totalTasks}
                  </span>
                </div>
                <div className="relative w-auto pl-4 flex-initial">
                  <div className="text-white p-3 text-center inline-flex items-center justify-center w-12 h-12 shadow-lg rounded-full bg-orange-500">
                    <i className="fas fa-tasks"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full lg:w-6/12 xl:w-3/12 px-4 mb-6">
          <div className="relative flex flex-col min-w-0 break-words bg-white rounded-lg shadow-lg">
            <div className="flex-auto p-4">
              <div className="flex flex-wrap">
                <div className="relative w-full pr-4 max-w-full flex-grow flex-1">
                  <h5 className="text-blueGray-400 uppercase font-bold text-xs mb-1">
                    En Progreso
                  </h5>
                  <span className="font-bold text-3xl text-blueGray-700">
                    {globalStats.working + globalStats.reviewing}
                  </span>
                </div>
                <div className="relative w-auto pl-4 flex-initial">
                  <div className="text-white p-3 text-center inline-flex items-center justify-center w-12 h-12 shadow-lg rounded-full bg-blue-500">
                    <i className="fas fa-cog fa-spin"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full lg:w-6/12 xl:w-3/12 px-4 mb-6">
          <div className="relative flex flex-col min-w-0 break-words bg-white rounded-lg shadow-lg">
            <div className="flex-auto p-4">
              <div className="flex flex-wrap">
                <div className="relative w-full pr-4 max-w-full flex-grow flex-1">
                  <h5 className="text-blueGray-400 uppercase font-bold text-xs mb-1">
                    Finalizadas
                  </h5>
                  <span className="font-bold text-3xl text-blueGray-700">
                    {globalStats.finished}
                  </span>
                </div>
                <div className="relative w-auto pl-4 flex-initial">
                  <div className="text-white p-3 text-center inline-flex items-center justify-center w-12 h-12 shadow-lg rounded-full bg-green-500">
                    <i className="fas fa-check"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="flex flex-wrap">
        <div className="w-full xl:w-6/12 px-4 mb-6">
          <div className="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 shadow-lg rounded-lg">
            <div className="rounded-t bg-white mb-0 px-6 py-6 border-b border-blueGray-100">
              <div className="text-center flex justify-between">
                <h6 className="text-blueGray-700 text-lg font-bold">
                  <i className="fas fa-chart-pie mr-2 text-purple-500"></i>
                  Distribución de Tareas
                </h6>
              </div>
            </div>
            <div className="p-6">
              <div className="relative" style={{ height: "300px" }}>
                <canvas id="statusChart"></canvas>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full xl:w-6/12 px-4 mb-6">
          <div className="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 shadow-lg rounded-lg">
            <div className="rounded-t bg-white mb-0 px-6 py-6 border-b border-blueGray-100">
              <div className="text-center flex justify-between">
                <h6 className="text-blueGray-700 text-lg font-bold">
                  <i className="fas fa-chart-bar mr-2 text-lightBlue-500"></i>
                  Comparativa de Proyectos
                </h6>
              </div>
            </div>
            <div className="p-6">
              <div className="relative" style={{ height: "300px" }}>
                <canvas id="projectsChart"></canvas>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Projects List */}
      <div className="flex flex-wrap">
        <div className="w-full px-4">
          <div className="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 shadow-lg rounded-lg">
            <div className="rounded-t bg-white mb-0 px-6 py-6 border-b border-blueGray-100">
              <div className="text-center flex justify-between">
                <h6 className="text-blueGray-700 text-lg font-bold">
                  <i className="fas fa-list mr-2 text-orange-500"></i>
                  Proyectos Activos ({dashboardStats.length})
                </h6>
              </div>
            </div>
            <div className="block w-full px-8 py-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {dashboardStats.map((project) => (
                  <div
                    key={project.id}
                    className="bg-white border-2 border-blueGray-200 rounded-xl p-8 hover:border-lightBlue-400 hover:shadow-xl transition-all duration-200"
                  >
                    {/* Project Header */}
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex-1">
                        <div
                          onClick={() => handleProjectClick(project.project_code)}
                          className="text-left group cursor-pointer"
                          role="button"
                          tabIndex={0}
                        >
                          <h4 className="text-2xl font-bold text-blueGray-700 group-hover:text-lightBlue-600 transition-colors cursor-pointer">
                            {project.project_name}
                            <i className="fas fa-external-link-alt ml-2 text-sm opacity-0 group-hover:opacity-100 transition-opacity"></i>
                          </h4>
                        </div>
                        <p className="text-base text-blueGray-500 mt-2">
                          <span className="font-semibold text-blueGray-700">{project.project_code}</span>
                          <span className="mx-2">•</span>
                          <span>{project.total_tasks} tarea{project.total_tasks !== 1 ? 's' : ''}</span>
                        </p>
                      </div>
                      <div className="flex-shrink-0 ml-6">
                        <div className="bg-lightBlue-100 text-lightBlue-700 rounded-xl px-4 py-3 text-center shadow-sm">
                          <div className="text-3xl font-bold">{project.total_tasks}</div>
                          <div className="text-xs uppercase font-semibold mt-1">Total</div>
                        </div>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    {project.total_tasks > 0 && (
                      <div className="mb-6">
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span className="text-blueGray-700 font-semibold">Progreso</span>
                          <span className="text-blueGray-600 font-medium">
                            {project.tasks_by_status.finished || 0} / {project.total_tasks}
                          </span>
                        </div>
                        <div className="relative w-full h-4 bg-blueGray-200 rounded-full overflow-hidden shadow-inner">
                          {Object.entries(project.tasks_by_status).map(([status, count]) => {
                            const percentage = (count / project.total_tasks) * 100;
                            return count > 0 ? (
                              <div
                                key={status}
                                className={`absolute h-full ${getStatusColor(status)} transition-all duration-300`}
                                style={{
                                  left: `${Object.entries(project.tasks_by_status)
                                    .slice(0, Object.keys(project.tasks_by_status).indexOf(status))
                                    .reduce((acc, [_, val]) => acc + (val / project.total_tasks) * 100, 0)}%`,
                                  width: `${percentage}%`
                                }}
                                title={`${getStatusLabel(status)}: ${count}`}
                              ></div>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}

                    {/* Task Status Pills */}
                    {project.total_tasks > 0 ? (
                      <div className="flex flex-wrap gap-3">
                        {Object.entries(project.tasks_by_status).map(([status, count]) => (
                          count > 0 && (
                            <div
                              key={status}
                              onClick={() => handleStatusClick(project.project_code, status)}
                              role="button"
                              tabIndex={0}
                              className={`
                                inline-flex items-center px-4 py-2.5 rounded-lg text-sm font-semibold
                                transition-all duration-150 cursor-pointer
                                border-2 border-transparent hover:border-current hover:shadow-md
                                ${getStatusBadgeColor(status)}
                              `}
                              title={`Ver tareas ${getStatusLabel(status).toLowerCase()}`}
                            >
                              <i className={`fas fa-circle text-xs mr-2 ${getStatusIconColor(status)}`}></i>
                              <span>{getStatusLabel(status)}</span>
                              <span className="ml-2 px-2.5 py-1 bg-white bg-opacity-60 rounded-md font-bold text-sm">
                                {count}
                              </span>
                            </div>
                          )
                        ))}
                      </div>
                    ) : (
                      <p className="text-base text-blueGray-400 italic text-center py-6">
                        <i className="fas fa-inbox mr-2"></i>
                        No hay tareas en este proyecto
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
